// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import * as React from 'react'
import Button from '@mui/material/Button'
import Icon from 'src/@core/components/icon'
import { Grid } from '@mui/material'
import CustomAvatar from 'src/@core/components/mui/avatar'
import { useRouter } from 'next/navigation'
import { useGetAllReminders } from 'src/hooks/reminders/useGetAllReminders'
import { useTheme, alpha } from '@mui/material/styles'
import { reminderCategories } from 'src/constants/reminderCategories'

interface UpcomingRemindersProps {
  cardStyles?: any
}

const UpcomingReminders = ({ cardStyles }: UpcomingRemindersProps) => {
  const reminderCardConfigs = reminderCategories
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const infoIconRef = React.useRef(null);
  const router = useRouter()
  const [filteredReminders, setFilteredReminders] = React.useState<any>([])
  const theme = useTheme()

  const {
    data: allReminders,
    isLoading,
    isError,
    refetch
  } = useGetAllReminders()

  const applyFilters = React.useCallback(() => {
    if (!allReminders) {
      setFilteredReminders([])
      return
    }

    const currentDate = new Date()

    const filtered = allReminders.filter((reminder: any) => {
      const expirationDate = new Date(reminder.expiration_date)
      return expirationDate > currentDate && reminder.isExpirationDateUpdated !== true
    })

    setFilteredReminders(filtered)
  }, [allReminders]);

  React.useEffect(() => {
    refetch();
    applyFilters();
  }, [allReminders, applyFilters, refetch]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    router.push('/reminders')
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateToFilteredReminders = (filterType: string) => {
    router.push(`/reminders?filter=${encodeURIComponent(filterType)}`);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  // Get icon based on document type
  const reminderSummaryCards = React.useMemo(
    () =>
      reminderCardConfigs.map(card => {
        const matchingReminders =
          filteredReminders?.filter((reminder: any) => card.matcher(reminder.file_type || '')) || []
        return { ...card, count: matchingReminders.length, reminders: matchingReminders }
      }),
    [filteredReminders]
  )

  return (
    <Card
      sx={{
        height: 'fit-content',
        backgroundColor: cardStyles?.backgroundColor || theme.palette.grey[50],
        border: cardStyles?.border || `1px solid ${theme.palette.grey[200]}`,
        boxShadow: 'none',
        borderRadius: 4
      }}
    >
      <Grid item xs={12}>
        <CardHeader
          color='white'
          title='Upcoming reminders'
          subheader='All expiring documents and licenses'
          subheaderTypographyProps={{ sx: { lineHeight: 1.429 } }}
          titleTypographyProps={{ sx: { letterSpacing: '0.15px' } }}
        />
        {filteredReminders && filteredReminders?.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 5 }}>
            <Typography variant='body2' color='text.secondary'>
              You have no upcoming reminders.
            </Typography>
          </Box>
        )}
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {reminderSummaryCards.map(card => (
              <Grid item xs={12} sm={6} md={4} key={card.key}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette[card.color]?.main || theme.palette.primary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette[card.color]?.main || theme.palette.primary.main, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 95,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[2],
                      transform: 'translateY(-2px)',
                      backgroundColor: alpha(theme.palette[card.color]?.main || theme.palette.primary.main, 0.14)
                    }
                  }}
                  onClick={() => navigateToFilteredReminders(card.key)}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant='body2' sx={{ color: theme.palette[card.color]?.dark || theme.palette.text.secondary, fontWeight: 600 }}>
                      {card.label}
                    </Typography>
                    <Typography variant='h4' sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                      {card.count}
                    </Typography>
                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                      Expiring within the set window
                    </Typography>
                  </Box>
                  <CustomAvatar
                    skin='light'
                    variant='rounded'
                    color={card.color}
                    sx={{ width: 48, height: 48 }}
                  >
                    <Icon icon={card.icon} width={24} height={24} />
                  </CustomAvatar>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 4 }}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleClick}
              startIcon={<Icon icon='mdi:view-list' />}
              sx={{ px: 4 }}
            >
              View All Reminders
            </Button>
          </Box>
        </CardContent>
      </Grid>
    </Card>
  )
}

export default UpcomingReminders
